import { useQuery } from '@tanstack/react-query';
import { customFieldsKeys } from './customFields.keys';
import { formulaFieldsApi } from './formulaFields.api';
import type {
  FormulaEvaluationResponse,
  FormulaTarget,
  FormulaValidationResponse,
} from './formulaFields.interface';

export const formulaEvaluationKeys = {
  validate: (subjectType: string, formula: string, target: FormulaTarget) =>
    [
      ...customFieldsKeys.all,
      'formula-validate',
      subjectType,
      formula,
      target.kind ?? '',
      target.fieldId ?? 'new',
      // The scope is part of the question: narrow the field and the same
      // formula earns a different answer.
      target.officeIds ?? [],
      target.departmentIds ?? [],
    ] as const,
  evaluate: (
    subjectType: string,
    formula: string,
    inputs: Record<string, unknown>,
    target: FormulaTarget
  ) =>
    [
      ...customFieldsKeys.all,
      'formula-evaluate',
      subjectType,
      formula,
      inputs,
      target.kind ?? '',
      target.fieldId ?? 'new',
      // Part of the question: `req.*` resolves against this requisition, so
      // changing it has to earn a fresh answer. The application is fixed for
      // the life of the form, but it keys the answer all the same.
      target.reqId ?? '',
      target.applicationId ?? '',
    ] as const,
};

interface EvaluationOptions {
  enabled?: boolean;
}

// The same verdict the save gives, so the editor cannot show green where the
// write refuses. A blank formula is not an error and is still not saveable, so
// the server answers `valid: false` with no diagnostics to underline.
export const useValidateFormula = (
  subjectType: string,
  formula: string,
  target: FormulaTarget = {},
  { enabled = true }: EvaluationOptions = {}
) =>
  useQuery<FormulaValidationResponse>({
    queryKey: formulaEvaluationKeys.validate(subjectType, formula, target),
    queryFn: () =>
      formulaFieldsApi.validateFormula(subjectType, formula, target),
    enabled: enabled && formula.trim() !== '',
    retry: false,
    // Keyed by the formula itself, so a verdict never goes stale.
    staleTime: Infinity,
  });

// Runs the formula server-side. The client engine reads whatever the widget
// holds — a percent as 10 where the offer stores 0.1, a date as epoch
// milliseconds — so only this answers in the units the save uses.
// `inputs` is keyed by each field's handle, which the response's own `inputs`
// list supplies: called with none, the server returns the fields to collect.
export const useEvaluateFormula = (
  subjectType: string,
  formula: string,
  inputs: Record<string, unknown> = {},
  target: FormulaTarget = {},
  { enabled = true }: EvaluationOptions = {}
) =>
  useQuery<FormulaEvaluationResponse>({
    queryKey: formulaEvaluationKeys.evaluate(
      subjectType,
      formula,
      inputs,
      target
    ),
    queryFn: () =>
      formulaFieldsApi.evaluateFormula(subjectType, formula, inputs, target),
    enabled: enabled && formula.trim() !== '',
    // One retry: a blip would otherwise leave the offer form with no value for
    // as long as it stays open, since a formula's answer never goes stale.
    retry: 1,
    staleTime: Infinity,
    // A new sample value should not blank the panel while its answer is in flight.
    placeholderData: (previous) => previous,
  });
